/**
 * Path Registry Utilities
 * 
 * This file contains utilities for working with the Path Registry system on the client side.
 * It provides functions for caching path definitions, building paths from definitions,
 * and getting signed URLs for resources using the path-based approach.
 */

import {
  getPathDefinitions,
  getSignedUrlForPath,
  generateS3Path,
  extractS3PathParams,
  isS3Path
} from './s3PathUtils';
import { fetchSignedUrlByType, fetchUserSignedUrlPath } from './fetchSignedUrlByFolderV2';
import { s3HttpsBase } from './s3KeyUtils';


// Cache for path definitions
let pathDefsCache = null;
let cacheInitialized = false;
let lastCacheRefresh = null;
const CACHE_TTL = 3600000; // 1 hour in milliseconds

/**
 * Initialize the path definitions cache
 * @returns {Promise<Array>} The path definitions
 */
export const initializePathDefsCache = async () => {
  try {
    console.log('Initializing path definitions cache');

    // Fetch all path definitions from the server
    const pathDefs = await getPathDefinitions();

    // Store in cache
    pathDefsCache = pathDefs;
    cacheInitialized = true;
    lastCacheRefresh = Date.now();

    console.log(`Cache initialized with ${pathDefs.length} path definitions`);
    return pathDefs;
  } catch (error) {
    console.error('Error initializing path definitions cache:', error);
    cacheInitialized = false;
    throw error;
  }
};

/**
 * Refresh the path definitions cache
 * @returns {Promise<Array>} The path definitions
 */
export const refreshPathDefsCache = async () => {
  try {
    console.log('Refreshing path definitions cache');
    return await initializePathDefsCache();
  } catch (error) {
    console.error('Error refreshing path definitions cache:', error);
    throw error;
  }
};

/**
 * Get the path definitions cache status
 * @returns {Object} The cache status
 */
export const getPathDefsCacheStatus = () => {
  return {
    initialized: cacheInitialized,
    lastRefresh: lastCacheRefresh ? new Date(lastCacheRefresh).toISOString() : null,
    size: pathDefsCache ? pathDefsCache.length : 0,
    ttl: CACHE_TTL,
    ttlHours: CACHE_TTL / 3600000
  };
};

/**
 * Get all path definitions from cache or server
 * @returns {Promise<Array>} The path definitions
 */
export const getAllPathDefinitions = async () => {
  try {
    // Check if cache is initialized and not expired
    if (cacheInitialized &&
      pathDefsCache &&
      (Date.now() - lastCacheRefresh) < CACHE_TTL) {
      console.log('Returning path definitions from cache');
      return pathDefsCache;
    }

    // If cache is not initialized or expired, refresh it
    console.log('Cache miss or expired, fetching path definitions from server');
    return await refreshPathDefsCache();
  } catch (error) {
    console.error('Error getting path definitions:', error);
    throw error;
  }
};

/**
 * Get a path definition by name from cache or server
 * @param {string} name - The name of the path definition
 * @returns {Promise<Object>} The path definition
 */
export const getPathDefinition = async (name) => {
  try {
    console.log(`Getting path definition: ${name}`);

    // Check if cache is initialized and not expired
    if (cacheInitialized &&
      pathDefsCache &&
      (Date.now() - lastCacheRefresh) < CACHE_TTL) {
      console.log(`Looking for path definition '${name}' in cache`);
      const pathDef = pathDefsCache.find(def => def.name === name);
      if (pathDef) {
        console.log(`Found path definition '${name}' in cache`);
        return pathDef;
      }
    }

    // If not found in cache or cache is expired, fetch all definitions
    // This is simpler than adding another API endpoint for getting a single definition
    console.log(`Path definition '${name}' not found in cache or cache expired, refreshing cache`);
    await refreshPathDefsCache();

    // Now try to find it in the refreshed cache
    if (pathDefsCache) {
      const pathDef = pathDefsCache.find(def => def.name === name);
      if (pathDef) {
        return pathDef;
      }
    }

    // If still not found, return null
    console.log(`Path definition '${name}' not found`);
    return null;
  } catch (error) {
    console.error(`Error getting path definition '${name}':`, error);
    throw error;
  }
};

/**
 * Extract parameters from a URL path using a path definition
 * @param {string} path - The URL path to extract parameters from
 * @param {string} resourceType - The resource type for Path Registry lookup
 * @returns {Promise<Object>} - Result object with success flag and extracted parameters
 */
export const extractParamsFromPath = async (path, resourceType) => {
  try {
    // Get the path definition from the registry (uses existing caching mechanism)
    const pathDef = await getPathDefinition(resourceType);
    if (!pathDef) {
      console.error(`[Path Registry] Definition for ${resourceType} not found. Check if it exists in the registry.`);
      return { success: false, error: `Path definition for ${resourceType} not found` };
    }

    // Split the path into parts
    const parts = path.split('/').filter(part => part.trim() !== '');

    // Extract parameters based on position in path
    const params = {};
    const missingParams = [];

    // Process each parameter defined in the path definition
    pathDef.params.forEach(param => {
      // If position is defined and valid
      if (param.position >= 0 && param.position < parts.length) {
        params[param.name] = parts[param.position];
      }
      // If it's a required parameter but not found
      else if (param.required && !params[param.name]) {
        missingParams.push(param.name);
        console.error(`[Path Registry] Required parameter '${param.name}' not found in path: ${path}`);
      }
      // If it has a default value and wasn't found in the path
      else if (param.defaultValue !== undefined && !params[param.name]) {
        params[param.name] = param.defaultValue;
      }
    });

    // Check if we have all required parameters
    const success = missingParams.length === 0;

    if (!success) {
      console.error(`[Path Registry] Missing required parameters for ${resourceType}: ${missingParams.join(', ')}`);
      console.error(`[Path Registry] Path: ${path}`);
      console.error(`[Path Registry] Path parts: ${JSON.stringify(parts)}`);
      console.error(`[Path Registry] Path definition: ${JSON.stringify(pathDef)}`);
    } else {
      console.log(`[Path Registry] Successfully extracted parameters for ${resourceType}: ${JSON.stringify(params)}`);
    }

    return {
      success,
      params,
      resourceType,
      error: success ? null : `Missing required parameters: ${missingParams.join(', ')}`
    };
  } catch (error) {
    console.error(`[Path Registry] Error extracting parameters from path:`, error);
    return {
      success: false,
      error: `Error extracting parameters: ${error.message}`
    };
  }
};

/**
 * Build a path from a path definition and parameters
 * @param {Object} pathDef - The path definition
 * @param {Object} params - The parameters to use for building the path
 * @returns {string} The built path
 */
export const buildPathFromDefinition = (pathDef, params) => {
  try {
    // Build path from definition

    if (!pathDef) {
      console.error('Path definition is required');
      throw new Error('Path definition is required');
    }

    if (!pathDef.pattern || !pathDef.pattern.requiredSegments) {
      console.error('Path definition must have a pattern with requiredSegments');
      throw new Error('Path definition must have a pattern with requiredSegments');
    }

    if (!params) {
      console.error('Parameters are required');
      throw new Error('Parameters are required');
    }

    // Check if we should use the folderTemplate instead of building from parts
    if (pathDef.pattern.folderTemplate) {
      console.log('Using folderTemplate:', pathDef.pattern.folderTemplate);

      // Check for template placeholders
      const templatePlaceholders = pathDef.pattern.folderTemplate.match(/\{([^}]+)\}/g) || [];
      console.log('Template placeholders:', templatePlaceholders);

      // Check if all placeholders have corresponding parameter values
      const missingPlaceholders = [];
      let templatedPath = pathDef.pattern.folderTemplate;

      templatePlaceholders.forEach(placeholder => {
        const paramName = placeholder.substring(1, placeholder.length - 1);
        console.log(`Checking parameter ${paramName} for placeholder ${placeholder}`);

        if (!params[paramName]) {
          missingPlaceholders.push(paramName);
        } else {
          console.log(`Replacing ${placeholder} with ${params[paramName]}`);
          templatedPath = templatedPath.replace(placeholder, params[paramName]);
        }
      });

      if (missingPlaceholders.length > 0) {
        const errorMsg = `Missing parameters for template placeholders: ${missingPlaceholders.join(', ')}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('Built path from template:', templatedPath);
      return templatedPath;
    }

    // If no folderTemplate, build from required segments and parameters
    console.log('Building path from required segments and parameters');
    console.log('Required segments:', pathDef.pattern.requiredSegments);

    // Start with the required segments
    const pathParts = [...pathDef.pattern.requiredSegments];
    console.log('Initial path parts:', pathParts);

    // Add parameter values based on the path definition
    if (pathDef.params && pathDef.params.length > 0) {
      console.log('Path definition params:', pathDef.params);

      // Validate that all required parameters are provided
      const missingParams = pathDef.params
        .filter(param => param.required)
        .filter(param => !params[param.name])
        .map(param => param.name);

      console.log('Missing required parameters:', missingParams);

      if (missingParams.length > 0) {
        const errorMsg = `Missing required parameters: ${missingParams.join(', ')}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Sort parameters by position
      const sortedParams = [...pathDef.params].sort((a, b) => a.position - b.position);
      console.log('Sorted parameters by position:', sortedParams);

      // Add parameter values to the path
      for (const param of sortedParams) {
        if (params[param.name]) {
          console.log(`Adding parameter ${param.name} with value ${params[param.name]} at position ${param.position}`);
          pathParts.push(params[param.name]);
        } else {
          console.log(`Parameter ${param.name} not provided, skipping`);
        }
      }
    }

    console.log('Final path parts:', pathParts);

    // Join the path parts with slashes
    const result = pathParts.join('/');
    console.log('Final built path:', result);

    return result;
  } catch (error) {
    console.error('Error building path from definition:', error);
    throw error;
  }
};

/**
 * Get a signed URL for a resource using the path-based approach
 * @param {string} userId - The user ID
 * @param {string} resourceType - The type of resource (path definition name)
 * @param {Object} params - The parameters to use for building the path
 * @param {string} [fileName] - Optional explicit filename to use
 * @returns {Promise<string>} The signed URL
 */
export const getResourceUrl = async (userId, resourceType, params, fileName = null) => {
  try {
    console.log(`Getting resource URL for ${resourceType} with params:`, params);
    console.log('userId:', userId);
    console.log('resourceType:', resourceType);
    console.log('params type:', typeof params);

    // Check for suspicious patterns in params
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'string') {
        if (value.includes('userId') || value.includes('adminId') || value.includes('actorId')) {
          console.warn(`WARNING: Parameter ${key} contains template placeholders: ${value}`);
        }
        if (value.includes('"') || value.includes("'")) {
          console.warn(`WARNING: Parameter ${key} contains quote characters: ${value}`);
        }
      }
    }

    // Get the path definition
    let pathDef = await getPathDefinition(resourceType);
    console.log('Path definition:', pathDef);

    if (!pathDef) {
      console.warn(`Path definition '${resourceType}' not found, refreshing cache and trying again`);

      // Try refreshing the cache and getting the path definition again
      try {
        await refreshPathDefsCache();
        pathDef = pathDefsCache.find(def => def.name === resourceType);
        console.log('Path definition after refresh:', pathDef);
      } catch (refreshError) {
        console.error(`Error refreshing path definitions cache:`, refreshError);
      }

      // If still not found, fall back to legacy approach
      if (!pathDef) {
        console.warn(`Path definition '${resourceType}' still not found after refresh, falling back to legacy approach`);
        return await legacyGetUrl(userId, resourceType, params);
      }
    }

    // Ensure all required parameters are present
    const missingParams = (pathDef.params || [])
      .filter(param => param.required && !params[param.name])
      .map(param => param.name);

    console.log('Required parameters check - missing params:', missingParams);

    if (missingParams.length > 0) {
      console.warn(`Missing required parameters for ${resourceType}: ${missingParams.join(', ')}`);
      console.warn(`Falling back to legacy approach for ${resourceType}`);
      return await legacyGetUrl(userId, resourceType, params);
    }

    try {
      // Log the path definition pattern
      console.log('Path definition pattern:', pathDef.pattern);

      // Build the path
      console.log('Building path from definition...');
      const path = buildPathFromDefinition(pathDef, params);
      console.log(`Built path: ${path}`);
      console.log('Path type:', typeof path);

      // Check for suspicious patterns in the built path
      if (path.includes('userId') || path.includes('adminId') || path.includes('actorId')) {
        console.warn('WARNING: Built path contains template placeholders instead of actual values:', path);
      }

      // Check for quote characters in the built path
      if (path.includes('"') || path.includes("'")) {
        console.warn('WARNING: Built path contains quote characters:', path);
      }

      // Get the signed URL
      console.log(`Getting signed URL for path: ${path}`);
      console.log(`Using explicit fileName: ${fileName || 'none provided'}`);

      // Pass the fileName as a separate parameter if provided
      const signedUrl = await getSignedUrlForPath(userId, path, resourceType, fileName);
      console.log(`Got signed URL: ${signedUrl}`);

      return signedUrl;
    } catch (pathError) {
      console.error(`Error building path or getting signed URL for ${resourceType}:`, pathError);
      // Fallback to legacy approach
      console.warn(`Falling back to legacy approach for ${resourceType}`);
      return await legacyGetUrl(userId, resourceType, params);
    }
  } catch (error) {
    console.error(`Error getting resource URL for ${resourceType}:`, error);

    // Fallback to legacy approach
    console.warn(`Falling back to legacy approach for ${resourceType}`);
    return await legacyGetUrl(userId, resourceType, params);
  }
};

/**
 * Legacy approach for getting a signed URL
 * This is a fallback for when the path-based approach fails
 * @param {string} userId - The user ID
 * @param {string} resourceType - The type of resource
 * @param {Object} params - The parameters to use
 * @returns {Promise<string>} The signed URL
 */
export const legacyGetUrl = async (userId, resourceType, params) => {
  try {
    console.log(`Using legacy approach for ${resourceType} with params:`, params);

    // Ensure userId is included in params
    const paramsWithUserId = {
      ...params,
      userId: params.userId || userId
    };

    try {
      // Try to generate a standardized S3 path
      const path = generateS3Path(resourceType, paramsWithUserId);
      console.log(`Generated standardized S3 path: ${path}`);
      return await fetchUserSignedUrlPath(userId, path);
    } catch (error) {
      console.error(`Error generating standardized S3 path for ${resourceType}:`, error);

      // Fall back to switch statement for backward compatibility
      switch (resourceType) {
        case 'adminProfileImage':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/administrators/${paramsWithUserId.adminId}/profile-img/${paramsWithUserId.fileName}`);

        case 'voiceActorProfileImage':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/aai-actors/${paramsWithUserId.actorId}/profile-img/${paramsWithUserId.fileName}`);

        case 'bookCreatorProfileImage':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/book-creator/${paramsWithUserId.creatorId}/profile-img/${paramsWithUserId.fileName}`);

        case 'literaryGroupProfileImage':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/lit-groups/${paramsWithUserId.litGroupId}/profile-img/${paramsWithUserId.fileName}`);

        case 'characterAvatarImage':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/aai-actors/${paramsWithUserId.actorId}/act-portfolios/${paramsWithUserId.portfolioId}/act-characters/${paramsWithUserId.characterId}/avatar/${paramsWithUserId.fileName}`);

        case 'variationAudio':
          return await fetchUserSignedUrlPath(userId, `aai-users/${paramsWithUserId.userId}/aai-actors/${paramsWithUserId.actorId}/act-portfolios/${paramsWithUserId.portfolioId}/act-characters/${paramsWithUserId.characterId}/act-variations/${paramsWithUserId.variationId}/${paramsWithUserId.fileName}`);

        default:
          // For unknown resource types, try using fetchSignedUrlByType directly
          return await fetchSignedUrlByType({
            requestType: 'FOLDER',
            userId,
            ...paramsWithUserId
          });
      }
    }
  } catch (error) {
    console.error(`Error in legacy approach for ${resourceType}:`, error);
    throw error;
  }
};

/**
 * Build a raw S3 key (no bucket prefix, no signing) from a PathRef name + params + fileName.
 * Mirrors the server-side `pathRegistryService.buildS3Key()`.
 *
 * @param {string} pathRefName - PathRef name (e.g. 'actVariationVoice')
 * @param {Object} params - Path parameters (e.g. { userId, actorId, portfolioId, ... })
 * @param {string} [fileName] - Optional filename to append to the folder path
 * @returns {Promise<string>} S3 key like 'aai-users/abc/aai-actors/def/.../file.wav'
 */
export const buildS3Key = async (pathRefName, params, fileName) => {
  const pathDef = await getPathDefinition(pathRefName);
  if (!pathDef) {
    throw new Error(`Path definition '${pathRefName}' not found in registry`);
  }
  const folderPath = buildPathFromDefinition(pathDef, params);
  return fileName ? `${folderPath}/${fileName}` : folderPath;
};

/**
 * Build a full S3 URL (bucket prefix + key, not signed) from a PathRef name + params + fileName.
 * Mirrors the server-side `pathRegistryService.buildS3Url()`.
 *
 * @param {string} pathRefName - PathRef name (e.g. 'actVariationVoice')
 * @param {Object} params - Path parameters
 * @param {string} [fileName] - Optional filename to append
 * @returns {Promise<string>} Full S3 URL (e.g. 'https://aai-api.s3.us-west-1.amazonaws.com/aai-users/...')
 */
export const buildS3Url = async (pathRefName, params, fileName) => {
  const key = await buildS3Key(pathRefName, params, fileName);
  return `${s3HttpsBase()}${key}`; // always-https base — never emit an s3:// asset URL
};


